import { Component, Output, EventEmitter, Input } from '@angular/core';
import {
	FormGroup,
	FormBuilder,
	ControlValueAccessor,
	NgControl,
	Validators,
} from '@angular/forms';
import { Observable, combineLatest } from 'rxjs';
import { map, takeUntil, tap, startWith } from 'rxjs/operators';
import { SelfDestruct } from 'src/shared/utilities/self-destruct';
import { CustomValidators } from 'src/shared/validators/custom-validators';
import { OtherChargeItem, ServiceType } from 'src/shared/api';
import { FormConfig } from 'src/shared/form-models/form-cva.component';

@Component({
	selector: 'rpfsa-other-charge',
	templateUrl: './other-charge.component.html',
	styleUrls: ['./other-charge.component.scss'],
})
export class OtherChargeComponent
	extends SelfDestruct
	implements ControlValueAccessor
{
	@Input() serviceTypes: ServiceType[] = [];
	@Input() currencyCode: string;
	@Output() save = new EventEmitter<Boolean>();
	@Output() cancel = new EventEmitter<Boolean>();

	amount$: Observable<number>;
	displayedColumns: string[] = [
		'autoRenew',
		'description',
		'chargeDate',
		'units',
		'unitPrice',
		'amount',
		'serviceType',
		'action',
	];
	initialState: any;

	form: FormGroup;
	config: FormConfig<OtherChargeItem> = {
		id: [''],
		jobId: [''],
		autoRenew: [''],
		description: [''],
		chargeDate: ['', Validators.required],
		units: ['', CustomValidators.mustBeNumeric],
		unitPrice: ['', CustomValidators.mustBeNumeric],
		serviceType: ['', Validators.required],
		amount: [''],
	};

	getChargeDateErrorMessage() {
		const field = this.form.get('chargeDate');
		return field.hasError('matDatepickerParse') ? 'Not a valid date' : '';
	}

	getUnitsErrorMessage() {
		const field = this.form.get('units');
		return field.hasError('isNotNumeric') ? 'Must be numeric' : '';
	}

	getUnitPriceErrorMessage() {
		const field = this.form.get('unitPrice');
		return field.hasError('isNotNumeric') ? 'Must be numeric' : '';
	}

	constructor(
		public ngControl: NgControl,
		public fb: FormBuilder // private readonly store: Store<JobDetailPartialState>
	) {
		super();
		ngControl.valueAccessor = this;
		this.form = fb.group(this.config);
		this.initialState = this.form.value;
	}
	private setupUnitTotalListeners() {
		const unitPriceCtrl = this.form.get('unitPrice');
		const unitsCtrl = this.form.get('units');

		combineLatest([
			unitsCtrl.valueChanges.pipe(startWith(unitsCtrl.value)),
			unitPriceCtrl.valueChanges.pipe(startWith(unitPriceCtrl.value)),
		])
			.pipe(
				takeUntil(this.destroyed),
				map(([units, unitPrice]) => {
					return units * unitPrice;
				}),
				tap((t) => {
					this.form.get('amount').setValue(t, { emitEvent: false });
				})
			)
			.subscribe();
	}

	writeValue(value: OtherChargeItem): void {
		if (value) {
			this.initialState = value;
			this.form.setValue(value, { emitEvent: false });
		}
		this.setupUnitTotalListeners();
	}

	registerOnChange(fn: any): void {
		this.form.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(fn);
	}
	registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	onTouched: () => void = () => {};

	saveForm() {
		this.save.emit(true);
	}

	cancelForm() {
		this.cancel.emit(true);
	}

	reset() {
		this.form.reset(this.initialState);
	}

	compareServiceTypes(st1: ServiceType, st2: ServiceType): boolean {
		return st1 && st2 ? st1.id === st2.id : st1 === st2; 
	}
}
